import {
	LegalLayout,
	type LegalSection,
} from '#app/components/marketing/legal-layout.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/aup.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Acceptable Use Policy - Syrcha Privacy Compliance Scanner',
		description:
			'Review our Acceptable Use Policy outlining prohibited uses and fair usage guidelines for Syrcha compliance monitoring services.',
		matches,
	})

export default function AcceptableUsePolicyRoute() {
	const sections: LegalSection[] = [
		{
			id: 'prohibited',
			title: 'Prohibited activities',
			body: (
				<>
					<p>You may not use Syrcha's Services to:</p>
					<ul>
						<li>
							<strong>Unauthorized scanning</strong>: scan or audit websites or
							apps without proper authorization from the owner.
						</li>
						<li>
							<strong>Security violations</strong>: violate or attempt to
							violate the security of any system or network.
						</li>
						<li>
							<strong>Malicious code</strong>: distribute malware, viruses, or
							other harmful code.
						</li>
						<li>
							<strong>Fraud</strong>: engage in phishing, fraud, or identity
							theft.
						</li>
						<li>
							<strong>IP infringement</strong>: infringe upon intellectual
							property rights.
						</li>
						<li>
							<strong>Harassment</strong>: harass, abuse, or threaten others.
						</li>
						<li>
							<strong>Service disruption</strong>: use automated systems to
							overload or disrupt our Services.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'fair',
			title: 'Fair usage',
			body: (
				<>
					<p>
						To ensure service quality for all users, fair usage guidelines
						apply:
					</p>
					<ul>
						<li>
							Free plans may be limited in the number of scans or data
							processed.
						</li>
						<li>
							Excessive or abusive usage that impacts service quality for others
							may result in suspension.
						</li>
						<li>API rate limits apply based on your subscription tier.</li>
						<li>
							Bulk scanning operations should be scheduled during off-peak hours
							when possible.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'monitoring',
			title: 'Monitoring and enforcement',
			body: (
				<>
					<p>We reserve the right to:</p>
					<ul>
						<li>Investigate violations of this AUP.</li>
						<li>Remove or disable access to content that violates this AUP.</li>
						<li>
							Suspend or terminate accounts for repeated or severe violations.
						</li>
						<li>
							Report illegal activities to appropriate law enforcement
							authorities.
						</li>
					</ul>
					<p>
						Enforcement actions are taken at our sole discretion based on the
						severity and frequency of violations.
					</p>
				</>
			),
		},
		{
			id: 'violations',
			title: 'Reporting violations',
			body: (
				<>
					<p>
						If you believe someone is violating this AUP, please report it to
						our abuse team:
					</p>
					<blockquote>
						<p>
							<strong>Syrcha Abuse Team</strong>
						</p>
						<p>
							Email: <a href="mailto:abuse@syrcha.com">abuse@syrcha.com</a>
						</p>
					</blockquote>
					<p>
						Please include as much detail as possible, including the account or
						URL involved and evidence of the violation.
					</p>
				</>
			),
		},
	]

	return (
		<LegalLayout
			eyebrow="/ legal · acceptable use"
			title={
				<>
					Acceptable use <span className="accent-underline">policy</span>.
				</>
			}
			effectiveDate="2025-09-01"
			lastUpdatedDate="2025-09-25"
			intro={
				<p>
					This Acceptable Use Policy ("AUP") outlines prohibited uses of
					Syrcha's Services. By using Syrcha, you agree to comply with this AUP.
				</p>
			}
			sections={sections}
		/>
	)
}
